import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { Box, ButtonBase, Typography } from "@mui/material";
import { ReactNode } from "react";

// Shared building blocks for entity "detail" screens (charging session / idling / reservation).
// Design system: yellow status pill + caret + dark id pill + Download, then a #1C1C1C info bar
// and a #1C1C1C label/value card.

const pillSx = {
  minHeight: "40px",
  borderRadius: "83px",
  p: "11px 13px 11px 20px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexShrink: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
} as const;

type SessionDetailHeaderProps = {
  statusIcon: ReactNode;
  statusLabel: string;
  entityIcon: ReactNode;
  entityId: string;
  onDownload?: () => void;
};

export const SessionDetailHeader = ({
  statusIcon,
  statusLabel,
  entityIcon,
  entityId,
  onDownload,
}: SessionDetailHeaderProps) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" gap="16px" flexWrap="wrap">
    <Box display="flex" alignItems="center" gap="10px">
      <Box sx={{ ...pillSx, bgcolor: "#FFFF00", color: "#0D0D0D" }}>
        <Box
          display="flex"
          width="18px"
          height="18px"
          flexShrink={0}
          sx={{ "& svg": { width: "100%", height: "100%" } }}
        >
          {statusIcon}
        </Box>
        <Typography fontSize="12px" fontWeight={700} lineHeight={1.2} px="6px">
          {statusLabel}
        </Typography>
      </Box>

      <KeyboardArrowRightRoundedIcon sx={{ fontSize: "19px", color: "#FFFFFF" }} />

      <Box sx={{ ...pillSx, bgcolor: "#0D0D0D", color: "#FFFFFF", maxWidth: "360px" }}>
        <Box
          display="flex"
          width="24px"
          height="24px"
          flexShrink={0}
          sx={{ "& svg": { width: "100%", height: "100%" } }}
        >
          {entityIcon}
        </Box>
        <Typography fontSize="13px" fontWeight={400} lineHeight={1.2} px="10px" noWrap>
          {entityId || "-"}
        </Typography>
        <KeyboardArrowDownRoundedIcon sx={{ fontSize: "19px", flexShrink: 0 }} />
      </Box>
    </Box>

    <ButtonBase
      onClick={onDownload}
      sx={{ ...pillSx, bgcolor: "#0D0D0D", color: "#C7C7C7" }}
    >
      <DownloadRoundedIcon sx={{ fontSize: "19px" }} />
      <Typography fontSize="12px" fontWeight={700} lineHeight={1.2} px="6px">
        Download
      </Typography>
    </ButtonBase>
  </Box>
);

export type SessionInfoField = {
  label: string;
  value: ReactNode;
  /** Grid column span — use for wide fields like "Address" (default 1). */
  span?: number;
};

// Fields flow in a wrapping row with an equal flex-basis, so as the container
// narrows they reflow into column-aligned rows (no ragged wrap, no truncation).
export const SessionDetailInfoBar = ({ fields }: { fields: SessionInfoField[] }) => (
  <Box
    bgcolor="#1C1C1C"
    borderRadius="15px"
    p="19px 22px 25px"
    display="flex"
    flexWrap="wrap"
    alignItems="flex-start"
    sx={{ columnGap: "28px", rowGap: "20px" }}
  >
    {fields.map((field) => {
      const span = field.span && field.span > 1 ? field.span : 1;
      return (
        <Box
          key={field.label}
          display="flex"
          flexDirection="column"
          gap="11px"
          minWidth={0}
          sx={{ flex: `${span} 1 ${span * 180}px` }}
        >
          <Typography fontSize="13px" fontWeight={400} lineHeight="0.984" color="#9B9B9B" noWrap>
            {field.label}
          </Typography>
          {typeof field.value === "string" || typeof field.value === "number" ? (
            <Typography
              fontSize="16px"
              fontWeight={600}
              lineHeight={1.3}
              color="#FFFFFF"
              sx={{ wordBreak: "break-word" }}
            >
              {field.value}
            </Typography>
          ) : (
            field.value
          )}
        </Box>
      );
    })}
  </Box>
);

export type SessionDetailRowItem = {
  label: string;
  value: ReactNode;
  valueColor?: string;
};

type SessionDetailCardProps = {
  rows: SessionDetailRowItem[];
  labelWidth?: string;
  columnGap?: string;
  contentPadding?: string;
};

export const SessionDetailCard = ({
  rows,
  labelWidth = "372px",
  columnGap = "0px",
  contentPadding = "22px 30px 26px",
}: SessionDetailCardProps) => (
  <Box
    bgcolor="#1C1C1C"
    borderRadius="14px"
    p={contentPadding}
    display="flex"
    flexDirection="column"
    gap="28px"
  >
    {rows.map((row) => (
      <Box key={row.label} display="flex" alignItems="center" gap={columnGap} width="100%">
        <Typography
          fontSize="13px"
          fontWeight={400}
          lineHeight="0.984"
          color="#9B9B9B"
          sx={{ flex: `0 1 ${labelWidth}`, minWidth: "130px" }}
        >
          {row.label}
        </Typography>
        <Box flex="1 1 0" minWidth={0}>
          {typeof row.value === "string" || typeof row.value === "number" ? (
            <Typography
              fontSize="13px"
              fontWeight={600}
              lineHeight={1.2}
              color={row.valueColor ?? "#FFFFFF"}
            >
              {row.value}
            </Typography>
          ) : (
            row.value
          )}
        </Box>
      </Box>
    ))}
  </Box>
);
